import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Trash2, X } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { formatMoney } from "@/components/tx-plan-center/pipelineHelpers";

function itemLabel(item) {
  if (!item) return null;
  const arch = String(item.arch || "").toLowerCase();
  const scope = arch === "upper"
    ? "Upper arch"
    : arch === "lower"
      ? "Lower arch"
      : item.tooth_number
        ? `Tooth #${item.tooth_number}`
        : null;
  return [scope, item.procedure_name || "Treatment item"].filter(Boolean).join(" · ");
}

export default function MvMistakeCorrectionDialog({
  open,
  onOpenChange,
  planId,
  item = null,
  procedures = [],
  onCorrected,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");

  const isItem = Boolean(item?.item_id);
  const targetLabel = useMemo(() => itemLabel(item), [item]);

  useEffect(() => {
    if (!open || !planId) return undefined;
    let cancelled = false;
    setPreview(null);
    setReason("");
    setConfirmed(false);
    setError("");
    setLoading(true);

    supabase.rpc("preview_bundle_mistake_correction", {
      p_plan_id: planId,
      p_item_id: item?.item_id ?? null,
    }).then(({ data, error: rpcError }) => {
      if (cancelled) return;
      setLoading(false);
      if (rpcError) {
        setError(rpcError.message || "Could not check whether this entry can be removed.");
        return;
      }
      setPreview(data ?? null);
    });

    return () => { cancelled = true; };
  }, [open, planId, item?.item_id]);

  if (!open) return null;

  const allowed = Boolean(preview?.allowed);
  const recorded = preview?.mode === "reverse_recorded";
  const canSubmit = allowed && confirmed && reason.trim().length >= 5 && !submitting;
  const actionLabel = isItem ? "Remove mistaken item" : "Remove mistaken treatment";
  const close = () => {
    if (!submitting) onOpenChange?.(false);
  };

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    const { data, error: rpcError } = await supabase.rpc("correct_bundle_mistake", {
      p_plan_id: planId,
      p_item_id: item?.item_id ?? null,
      p_reason: reason.trim(),
    });
    setSubmitting(false);
    if (rpcError) {
      setError(rpcError.message || "Could not remove this mistaken entry.");
      return;
    }
    await onCorrected?.(data);
    onOpenChange?.(false);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mv-mistake-title"
      data-testid="mv-mistake-correction-dialog"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/45 backdrop-blur-sm p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 id="mv-mistake-title" className="flex items-center gap-2 text-base font-bold text-slate-900">
              <Trash2 className="h-4 w-4 text-rose-600" />
              {isItem ? "Remove this mistaken item?" : "Remove this mistaken treatment?"}
            </h2>
            <p className="mt-1 text-[12px] leading-relaxed text-slate-600">
              Use this only when the entry itself was a mistake. Do not use it when the patient stopped or declined real treatment.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            aria-label="Close"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[65vh] space-y-4 overflow-y-auto px-5 py-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            {isItem ? (
              <p className="text-sm font-semibold text-slate-900">{targetLabel}</p>
            ) : (
              <>
                <p className="text-sm font-semibold text-slate-900">Entire ongoing treatment</p>
                <div className="mt-1 space-y-0.5">
                  {procedures.map((procedure) => (
                    <p key={procedure.item_id} className="text-[12px] text-slate-600">
                      {itemLabel(procedure)}
                    </p>
                  ))}
                </div>
              </>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking the clinic record…
            </div>
          ) : null}

          {!loading && preview ? (
            allowed ? (
              <div
                data-testid="mv-mistake-consequence"
                className={`rounded-lg border px-3 py-3 ${
                  recorded
                    ? "border-amber-200 bg-amber-50 text-amber-950"
                    : "border-emerald-200 bg-emerald-50 text-emerald-950"
                }`}
              >
                <p className="flex items-center gap-1.5 text-sm font-semibold">
                  {recorded ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                  {recorded ? "Recorded work will be reversed" : "No completed work or billing was found"}
                </p>
                <p className="mt-1 text-[12px] leading-relaxed">
                  {recorded
                    ? `The clinical record stays in history. The system will reverse ${formatMoney(Number(preview.active_credit_centavos || 0))} of production credit and remove ${isItem ? "this item" : "this treatment"} from active work.`
                    : `${isItem ? "This item" : "This treatment"} will disappear from active work. A private correction record will remain for accountability.`}
                </p>
                {recorded ? (
                  <p className="mt-1 text-[11px] font-medium">
                    {Number(preview.recorded_visit_count || 0)} recorded visit{Number(preview.recorded_visit_count || 0) === 1 ? "" : "s"} will remain visible in history.
                  </p>
                ) : null}
              </div>
            ) : (
              <div data-testid="mv-mistake-blocked" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-rose-950">
                <p className="flex items-center gap-1.5 text-sm font-semibold">
                  <AlertTriangle className="h-4 w-4" />
                  This cannot be removed here
                </p>
                <p className="mt-1 text-[12px] leading-relaxed">
                  This treatment has locked records. Use the proper billing or clinical correction instead.
                </p>
                {(preview.blockers || []).length > 0 ? (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-[12px]">
                    {preview.blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}
                  </ul>
                ) : null}
              </div>
            )
          ) : null}

          {allowed && !loading ? (
            <>
              <div>
                <label htmlFor="mv-mistake-reason" className="block text-[12px] font-semibold text-slate-700">
                  What was the mistake? <span className="text-rose-600">Required</span>
                </label>
                <textarea
                  id="mv-mistake-reason"
                  rows={3}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  data-testid="mv-mistake-reason"
                  placeholder="Example: Wrong patient selected while testing the app."
                  className="mt-1 w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                />
              </div>

              <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-slate-200 px-3 py-2.5 text-[12px] leading-relaxed text-slate-700">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(event) => setConfirmed(event.target.checked)}
                  data-testid="mv-mistake-confirm"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
                />
                <span>I confirm this was entered by mistake and should be removed from active treatment.</span>
              </label>
            </>
          ) : null}

          {error ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800" role="alert">
              {error}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-3">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            {allowed ? "Keep it" : "Close"}
          </button>
          {allowed ? (
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="mv-mistake-submit"
              className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {actionLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
